import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { locationKeyFromName } from '../lib/schedule.js';
import { ymdLocal } from '../lib/format.js';

// Weekly coverage heatmap (CJ 2026-07-29). Mon-Sun columns, 5a-6p hour rows.
// Green = fully staffed for that hour; red once availability drops below 2x the
// template's need (200%). The colour shifts per person lost so a single missing
// availability is visible, and full green means every regular is free that hour.

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const fmtHour = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`;

function cellColor(c) {
  if (!c || c.closed) return '#eceff1';                       // closed
  const need = c.need, pool = Math.max(c.pool || 0, 1), avail = c.avail || 0;
  if (need === 0) return avail > 0 ? '#eef7ef' : '#f5f5f5';   // no requirement this hour
  const threshold = 2 * need;                                 // 200% of need
  if (avail < threshold) {                                    // red → orange
    const t = Math.max(0, Math.min(1, avail / threshold));
    return `hsl(${Math.round(t * 35)}, 80%, ${Math.round(46 + t * 8)}%)`;
  }
  const t = Math.min(1, (avail - threshold) / Math.max(1, pool - threshold)); // green ramp
  return `hsl(${Math.round(90 + t * 40)}, 55%, ${Math.round(63 - t * 18)}%)`; // yellow-green → deep green
}

export function AvailabilityHeatmap({ user, location }) {
  const headers = { 'x-user-id': String(user.id) };
  const locKey = location ? locationKeyFromName(location.name) : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [offset, setOffset] = useState(0);   // weeks from the current week (0..7)

  const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
  const baseMonday = mondayOf(new Date());
  const weekMonday = new Date(baseMonday); weekMonday.setDate(baseMonday.getDate() + offset * 7);
  const weekYmd = ymdLocal(weekMonday);

  async function load() {
    if (!locKey) return;
    setLoading(true); setErr('');
    try { setData(await api('GET', `/api/health/heatmap/${locKey}?week=${weekYmd}`, null, headers)); }
    catch (e) { let m = e.message || 'Failed to load'; try { m = JSON.parse(m).error || m; } catch {} setErr(m); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [locKey, offset]);

  const weeks = Array.from({ length: 8 }, (_, i) => {
    const m = new Date(baseMonday); m.setDate(baseMonday.getDate() + i * 7);
    return { i, label: m.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
  });

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Coverage heatmap — availability by hour</div>
          <div style={{ fontSize: 11, color: '#757575' }}>Green = every regular free · red = fewer than 2× the template's need for that hour</div>
        </div>
        <select value={offset} onChange={(e) => setOffset(Number(e.target.value))}
          style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid #d0d4d9', fontFamily: 'inherit' }}>
          {weeks.map((w) => <option key={w.i} value={w.i}>{w.i === 0 ? 'This week' : 'Week of ' + w.label}</option>)}
        </select>
      </div>
      {err && <div style={{ fontSize: 12, color: '#c62828' }}>{err}</div>}
      {loading && !data && <div style={{ fontSize: 12, color: '#757575' }}>Loading…</div>}
      {data && (
        <div style={{ overflowX: 'auto' }}>
          {/* CJ 2026-07-30: fit mobile without horizontal scroll — fixed layout
              distributes the 7 day columns across the full width; the fixed
              520px min-width (which forced the scroll) is gone. */}
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: 24 }}></th>
                {data.days.map((d, di) => {
                  const dt = new Date(d.ymd + 'T12:00:00Z');
                  return (
                    <th key={d.ymd} style={{ fontSize: 11, fontWeight: 700, color: '#37474f', padding: '2px 4px', textAlign: 'center' }}>
                      {DOW[di]}<div style={{ fontSize: 9, color: '#9e9e9e', fontWeight: 400 }}>{dt.getUTCMonth() + 1}/{dt.getUTCDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.hours.map((h, ri) => (
                <tr key={h}>
                  <td style={{ fontSize: 9, color: '#757575', padding: '0 3px', whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtHour(h)}</td>
                  {data.days.map((d, di) => {
                    const c = d.cells[ri];
                    const short = c && !c.closed && c.need > 0 && c.avail < 2 * c.need;
                    const title = c && !c.closed
                      ? `${DOW[di]} ${fmtHour(h)} — ${c.avail} available, need ${c.need}${c.need ? ` (${Math.round((c.avail / c.need) * 100)}%)` : ''}`
                      : 'Closed';
                    return (
                      <td key={d.ymd} title={title}
                        style={{ background: cellColor(c), border: '1px solid #fff', height: 20, padding: 0, textAlign: 'center' }}>
                        {short ? <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>{c.avail}</span> : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: '#757575', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><i style={{ width: 12, height: 12, borderRadius: 2, background: cellColor({ need: 2, pool: 8, avail: 8 }) }} /> full</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><i style={{ width: 12, height: 12, borderRadius: 2, background: cellColor({ need: 2, pool: 8, avail: 4 }) }} /> at 200%</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><i style={{ width: 12, height: 12, borderRadius: 2, background: cellColor({ need: 2, pool: 8, avail: 3 }) }} /> below 200% (number = who's left)</span>
          </div>
        </div>
      )}
    </div>
  );
}
